/**
 * WorkflowTypeSelector
 *
 * Step 1 of the visitor registration flow.
 * Displays large tap-target buttons for each active VisitorPurpose,
 * grouped by workflow_type with a distinct icon per type.
 *
 * HCI principles:
 *  - Minimum 80px touch targets for tablet / kiosk use
 *  - Icons provide fast visual recognition — reducing reading load
 *  - Active selection is immediately highlighted
 *  - "Other / Custom" always appears last
 *
 * Receives a callback `onSelect(purposeId, workflowType)`.
 * After selection the parent advances to step 2 (DynamicVisitorForm).
 */
import { cn } from '@/lib/utils'
import {
  Users,
  HardHat,
  Package,
  ClipboardList,
  UserCheck,
  LayoutGrid,
} from 'lucide-react'
import type { VisitorPurpose, WorkflowType } from '@/types'

// ── Icon map per workflow type ────────────────────────────────────────────────

const WORKFLOW_ICONS: Record<WorkflowType, React.ElementType> = {
  GUEST:       Users,
  CONTRACTOR:  HardHat,
  DELIVERY:    Package,
  INTERVIEW:   ClipboardList,
  EMPLOYEE:    UserCheck,
  CUSTOM:      LayoutGrid,
}

const WORKFLOW_COLORS: Record<WorkflowType, string> = {
  GUEST:       'bg-blue-50   text-blue-600   border-blue-200   hover:bg-blue-100   data-[selected]:bg-blue-600   data-[selected]:text-white   data-[selected]:border-blue-600',
  CONTRACTOR:  'bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100 data-[selected]:bg-orange-600 data-[selected]:text-white data-[selected]:border-orange-600',
  DELIVERY:    'bg-amber-50  text-amber-600  border-amber-200  hover:bg-amber-100  data-[selected]:bg-amber-600  data-[selected]:text-white  data-[selected]:border-amber-600',
  INTERVIEW:   'bg-purple-50 text-purple-600 border-purple-200 hover:bg-purple-100 data-[selected]:bg-purple-600 data-[selected]:text-white data-[selected]:border-purple-600',
  EMPLOYEE:    'bg-green-50  text-green-600  border-green-200  hover:bg-green-100  data-[selected]:bg-green-600  data-[selected]:text-white  data-[selected]:border-green-600',
  CUSTOM:      'bg-slate-50  text-slate-600  border-slate-200  hover:bg-slate-100  data-[selected]:bg-slate-600  data-[selected]:text-white  data-[selected]:border-slate-600',
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface WorkflowTypeSelectorProps {
  /** All active VisitorPurposes for this org */
  purposes: VisitorPurpose[]
  /** Currently selected purpose id (controlled) */
  selectedPurposeId: string | null
  /** Called when user taps a purpose tile */
  onSelect: (purposeId: string, workflowType: WorkflowType) => void
}

// ── Component ─────────────────────────────────────────────────────────────────

export function WorkflowTypeSelector({
  purposes,
  selectedPurposeId,
  onSelect,
}: WorkflowTypeSelectorProps) {
  // Only show active purposes, sorted: non-CUSTOM first, then by name
  const sorted = [...purposes]
    .filter((p) => p.is_active)
    .sort((a, b) => {
      if (a.workflow_type === 'CUSTOM' && b.workflow_type !== 'CUSTOM') return 1
      if (a.workflow_type !== 'CUSTOM' && b.workflow_type === 'CUSTOM') return -1
      return a.name.localeCompare(b.name)
    })

  if (sorted.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        No visit types configured. Ask your administrator to set up visit purposes.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-foreground">
        Select visit type
      </p>
      <div
        className="grid gap-3"
        style={{
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        }}
        role="listbox"
        aria-label="Visit type"
      >
        {sorted.map((purpose) => {
          const Icon  = WORKFLOW_ICONS[purpose.workflow_type] ?? LayoutGrid
          const color = WORKFLOW_COLORS[purpose.workflow_type] ?? WORKFLOW_COLORS.CUSTOM
          const isSelected = purpose.id === selectedPurposeId

          return (
            <button
              key={purpose.id}
              type="button"
              role="option"
              aria-selected={isSelected}
              data-selected={isSelected ? '' : undefined}
              onClick={() => onSelect(purpose.id, purpose.workflow_type)}
              className={cn(
                'flex flex-col items-center justify-center gap-2.5 rounded-xl border-2',
                'min-h-[100px] p-4 cursor-pointer transition-all duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'select-none text-center',
                color,
              )}
            >
              <Icon className="h-7 w-7 shrink-0" aria-hidden />
              <span className="text-xs font-semibold leading-tight">
                {purpose.name}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
